import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const RULES = [
  { id: "len",     label: "At least 8 characters",  test: (p) => p.length >= 8 },
  { id: "upper",   label: "1 uppercase letter",      test: (p) => /[A-Z]/.test(p) },
  { id: "num",     label: "1 number",                test: (p) => /\d/.test(p) },
  { id: "special", label: "1 special character",     test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
  { id: "nospace", label: "No spaces",               test: (p) => p.length > 0 && !/\s/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Treat missing token as invalid up-front; flip on a server rejection too.
  const [tokenInvalid, setTokenInvalid] = useState(!token);

  const ruleResults = RULES.map((r) => ({ ...r, ok: r.test(pw) }));
  const allRulesPass = ruleResults.every((r) => r.ok);
  const matchOk = pw.length > 0 && pw === pw2;
  const canSubmit = allRulesPass && matchOk && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not reset password.";
      if (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("expired")) {
        setTokenInvalid(true);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Guard for missing/bad token
  if (tokenInvalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] shadow-xl p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-[#FEF2F2] mx-auto flex items-center justify-center mb-5">
            <ShieldAlert className="h-7 w-7 text-[#B91C1C]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0F1F3D]">Reset link is invalid or expired</h1>
          <p className="text-sm text-[#475569] mt-3">
            This link is no longer valid. Please request a new reset email from the sign-in page.
          </p>
          <Link
            to="/login"
            className="inline-block mt-6 px-5 py-2.5 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] shadow-xl p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-[#ECFDF5] mx-auto flex items-center justify-center mb-5">
            <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0F1F3D]">Password updated</h1>
          <p className="text-sm text-[#475569] mt-3">
            Redirecting you to the sign-in page…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] shadow-xl p-8"
        data-testid="reset-password-form"
      >
        <h1 className="font-display text-2xl font-bold text-[#0F1F3D]">Set a new password</h1>
        <p className="text-sm text-[#475569] mt-2">
          Pick a strong password you haven&apos;t used before.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#64748B]">New password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => /\s/.test(e.target.value) ? null : setPw(e.target.value)}
                data-testid="new-password-input"
                className="mt-2 w-full px-4 py-2.5 pr-11 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-[#94A3B8] hover:text-[#475569]"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#64748B]">Confirm password</label>
            <input
              type={showPw ? "text" : "password"}
              value={pw2}
              onChange={(e) => /\s/.test(e.target.value) ? null : setPw2(e.target.value)}
              data-testid="confirm-password-input"
              className="mt-2 w-full px-4 py-2.5 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] text-sm"
            />
            {pw2.length > 0 && !matchOk && (
              <p className="mt-1.5 text-xs text-[#B91C1C]">Passwords don&apos;t match.</p>
            )}
          </div>

          {/* Live rules */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 mt-1">
            {ruleResults.map((r) => (
              <li key={r.id} className={`flex items-center gap-1.5 text-xs ${r.ok ? "text-[#047857]" : "text-[#64748B]"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${r.ok ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} />
                {r.label}
              </li>
            ))}
          </ul>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="reset-password-submit"
            className="w-full px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F] disabled:opacity-60 transition-colors"
          >
            {busy ? "Updating…" : "Update password"}
          </button>

          <Link to="/login" className="block text-center text-sm text-[#475569] hover:underline">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
